<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Defining_Scope_Rules/Namespace_Alternatives';
    let nextLink= '/Documentation/Typing_and_Validating';
    
    import Note from "$lib/notes/Note.svelte";
    import Figure from "$lib/figures/Figure.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="the-freon-scoper-definition-1"
  bind:intersecting={visibleById["the-freon-scoper-definition-1"]}
  onIntersectChange={(visible) => setVisible("the-freon-scoper-definition-1", visible)}
>The Freon Scoper Definition</SectionComponent>
<p>The <strong>scope provider</strong> (or <strong>scoper</strong> for short) is defined by a scoper definition, which must be in a file with
the extension <code>.scope</code>. The scoper definition holds all your DSL’s scoping rules. All files with the .scope extension in
the <a
  href="/Documentation/Overview/Getting_Started#creating-a-project-2"
><em>defs</em> folder</a> (i.e. the folder where you keep your definition files) are combined into one scoper definition.
Any <code>.scope</code> file should start by declaring the language for which the scoper is defined.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/scoper-docu.scope#L1-L2

scoper for language InsuranceModel
</code></pre>
<p>The default scoper, which is the scoper provided when no scoper definition is given,
simply regards your user’s model as the one and only namespace, meaning that every node is visible everywhere.</p>
<SectionComponent tag="h2" 
  id="defining-namespaces-2"
  bind:intersecting={visibleById["defining-namespaces-2"]}
  onIntersectChange={(visible) => setVisible("defining-namespaces-2", visible)}
>Defining Namespaces</SectionComponent>
<p>Often, much of the necessary scoping can be defined simply by stating that certain concepts are namespaces. This is done in a single statement, which must come
before any other rules. All metatypes that are regarded as namespaces are listed between curly brackets after the keyword <code>isNamespace</code>.
Model units and interfaces can be used as well. Any instance of a concept that implements a namespace interface will
be regarded as a namespace.</p>
<p>Have a look again at an earlier example:</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/scoper-docu.scope#L3-L3

isNamespace &#123; InsuranceProduct, BaseProduct, CalcFunction, Entity, AttributeRef &#125;</code></pre>
<SectionComponent tag="h2" 
  id="defining-imports-3"
  bind:intersecting={visibleById["defining-imports-3"]}
  onIntersectChange={(visible) => setVisible("defining-imports-3", visible)}
>Defining Imports</SectionComponent>
<p>The next parts of the scoper definition cover imports and alternatives; they may be mixed. </p>
<p>To define an import use the name of the concept, model unit, or interface that identifies the namespace,
and - between curly brackets - add the imported namespace after the keyword <code>imports</code>. You may add a
list of properties; all of them will be imported. The keyword <code>recursive</code>
(See <a
  href="/Documentation/Defining_Scope_Rules/Namespace_Imports#recursive-imports-4"
>Recursive Imports</a>) may
be added to each entry in the list individually.</p>
<pre class="language-freon"><code class="language-freon">InsuranceProduct &#123;
    imports &#123;
        self.basedOn;
        recursive self.themes;
        self.parts;
    &#125; 
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="defining-alternatives-4"
  bind:intersecting={visibleById["defining-alternatives-4"]}
  onIntersectChange={(visible) => setVisible("defining-alternatives-4", visible)}
>Defining Alternatives</SectionComponent>
<p>To define an alternative, you should use
the name of the concept, model unit or interface that identifies the namespace, and add the imported namespace after
the keyword <code>alternative</code>. Here too, you may add a list of properties to be included in the alternatives, and
use the keyword <code>recursive</code> for each entry in the list individually.</p>
<pre class="language-freon"><code class="language-freon">InsuranceProduct &#123;
    alternatives &#123;
        self.basedOn;
        recursive self.themes;
        self.parts;
    &#125; 
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="overview-of-possibilities-in-the-scope-definition-5"
  bind:intersecting={visibleById["overview-of-possibilities-in-the-scope-definition-5"]}
  onIntersectChange={(visible) => setVisible("overview-of-possibilities-in-the-scope-definition-5", visible)}
>Overview of Possibilities in the Scope Definition</SectionComponent>
<p>The Freon meta-language for scoping provides
a number of options for building an expression to indicate the required namespace.</p>
<p>First, you may use dots to traverse the AST.</p>
<pre class="language-freon"><code class="language-freon">    alternatives &#123;
        self.xxx.yyy.rrr;
    &#125; </code></pre>
<p>Second, you can use an instance of a limited concept. Put a <code>#</code> sign before the name of the limited concept, and
use the <code>:</code> sign as separator followed by the name of the limited instance.</p>
<pre class="language-freon"><code class="language-freon">    imports &#123;
        #AttributeType:Integer;
    &#125; </code></pre>
<p>Third, you may want to use the owning namespace of a property. For this you should use the predefined function <code>owner()</code>.</p>
<pre class="language-freon"><code class="language-freon">    imports &#123;
        self.xxx.owner();
    &#125; </code></pre>
<p>Fourth, you can indicate that you want to use a certain property, but only
if it is of a certain metatype. To do this you use the predefined function <code>if()</code> which takes as its parameter the name of a
concept or model unit (no interfaces!). If the node is not of the required type, no namespace is added.</p>
<pre class="language-freon"><code class="language-freon">    imports &#123;
        self.xxx.if(ConceptY);
        self.if(Unit12);
    &#125; </code></pre>
<p>You can combine all the options and make a really complex statement. But be careful, it is easy to get confused.
In the following example, the namespace that is imported is the namespace indicated by the <code>conceptA_prop</code> instance of
the namespace that ‘owns’ the property <code>self.xxx</code> (i.e. <code>self.xxx</code> is
in the declared nodes of this namespace), but only if this owner is of (meta)type <code>ConceptA</code>.</p>
<pre class="language-freon"><code class="language-freon">    imports &#123;
        self.xxx.owner().if(ConceptA).conceptA_prop;
    &#125; </code></pre>
<p>Finally, when you have also defined a typer (See <a
  href="/Documentation/Typing_and_Validating"
>Typing and Validating</a>), you may
use the type of an instance as namespace. To do this you use the predefined function <code>type()</code>. Note that nothing may follow
a <code>type()</code> expression. Note also that the type of an instance is different from its metatype. The first is an instance of some
concept that is defined by the typer definition to represent the type of the node. The second is a concept, model unit, or
interface that is defined in the .ast file. </p>
<pre class="language-freon"><code class="language-freon">    alternatives &#123;
        self.xxx.type();
        self.type();
        self.yyy.owner().type();
    &#125; </code></pre>
<SectionComponent tag="h2" 
  id="example-scope-definition-6"
  bind:intersecting={visibleById["example-scope-definition-6"]}
  onIntersectChange={(visible) => setVisible("example-scope-definition-6", visible)}
>Example Scope Definition</SectionComponent>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/scoper-docu.scope

scoper for language InsuranceModel

isNamespace &#123; InsuranceProduct, BaseProduct, CalcFunction, Entity, AttributeRef &#125;

InsuranceProduct &#123;
    imports &#123;
        self.basedOn;
    &#125; 
&#125;

AttributeRef &#123;
	alternatives &#123;
        self.owner().type();
    &#125; 
&#125;

Entity &#123;
    imports &#123;
        self.baseEntity;
    &#125; 
&#125;
</code></pre>


<PrevNextSection {prevLink} {nextLink} />